import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError, type User } from "../api";
import { btnPrimary } from "../icons";
import {
  AuthLegalFooter,
  Caption,
  DESKTOP_CARD,
  Field,
  Subtitle,
  Title,
  mobileWrap,
} from "./auth-ui";

interface Props {
  isDesktop: boolean;
  onSignedIn: (user: User) => void;
  onSwitchToSignup: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
}

export function LoginScreen({
  isDesktop,
  onSignedIn,
  onSwitchToSignup,
  onOpenPrivacy,
  onOpenTerms,
}: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = email.trim().length > 0 && password.length >= 8 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const user = await api.login({ email: email.trim(), password });
      onSignedIn(user);
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVALID_CREDENTIALS") {
        setError(t("errors.invalidCredentials"));
      } else {
        setError(e instanceof Error ? e.message : t("errors.signInFailed"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div style={isDesktop ? DESKTOP_CARD : mobileWrap("60px 24px 30px")}>
      <Caption>{t("auth.signIn")}</Caption>
      <Title>{t("auth.welcomeBack")}</Title>
      <Subtitle>{t("auth.signInSubtitle")}</Subtitle>

      <Field
        label={t("auth.email")}
        type="email"
        inputMode="email"
        value={email}
        onChange={(v) => {
          setEmail(v);
          setError(null);
        }}
        placeholder={t("auth.emailPlaceholder")}
        autoFocus
      />

      <Field
        label={t("auth.password")}
        type="password"
        value={password}
        onChange={(v) => {
          setPassword(v);
          setError(null);
        }}
        placeholder={t("auth.passwordPlaceholder")}
        onSubmit={submit}
      />

      {error && (
        <div style={{ marginTop: -6, marginBottom: 10, fontSize: 13, color: "var(--danger)" }}>
          {error}
        </div>
      )}

      <div style={{ flex: 1 }} />

      <button disabled={!canSubmit} onClick={submit} style={btnPrimary(canSubmit)}>
        {submitting ? t("auth.signingIn") : t("auth.signIn")}
      </button>

      <button
        onClick={onSwitchToSignup}
        style={{
          marginTop: 14,
          background: "transparent",
          border: "none",
          color: "var(--ink-2)",
          fontSize: 14,
          fontWeight: 500,
          textAlign: "center",
        }}
      >
        {t("auth.noAccountPrefix")}{" "}
        <span style={{ color: "var(--ink)", fontWeight: 600 }}>{t("auth.createAccount")}</span>
      </button>

      <AuthLegalFooter onOpenPrivacy={onOpenPrivacy} onOpenTerms={onOpenTerms} />
    </div>
  );
}
